import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  query,
  where,
  limit,
  orderBy,
  getDocs,
  collection,
  getFirestore,
} from "firebase/firestore";
import { RootState } from "app/store";
import firebase, { fetchAuthToken } from "app/Auth";
import { client } from "common/apiClient";
import { ChatRoom, ChatRoomType } from "./types";

/**
 * 保護者用チャットルーム一覧取得
 */
export const getParentChatRooms = createAsyncThunk(
  "message/getParentChatRooms",
  async ({ uid }: { uid: string }) => {
    const token = await fetchAuthToken();
    const response = await client({ token }).get(`/v1/chat/room/${uid}`);
    return response.data.data;
  },
);

/**
 * 保護者のチャットルーム一覧を取得（差分追加用）
 * Sliceを更新しない単純なAPI呼び出し
 */
export const fetchParentChatRooms = async ({ uid }: { uid: string }) => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get(`/v1/chat/room/${uid}`);
  return response.data.data.chat_rooms as ChatRoom[];
};

export const getParentChatRoomMessages = createAsyncThunk(
  "message/getParentChatRoomMessages",
  async (
    {
      roomId,
      lastMessageSyncAt,
      messageCount,
      chatRoomType,
    }: {
      roomId: string;
      lastMessageSyncAt: string;
      messageCount: number;
      chatRoomType: ChatRoomType;
    },
    thunkAPI,
  ) => {
    const state = thunkAPI.getState() as RootState;
    const db = getFirestore(firebase);

    // talk room 以外は制限チェックしない
    if (chatRoomType !== ChatRoomType.TALK) {
      const queryMessage = query(
        collection(db, "rooms", roomId, "messages"),
        where("created_at", "<", lastMessageSyncAt),
        orderBy("created_at", "desc"),
        limit(messageCount),
      );
      const querySnapshot = await getDocs(queryMessage);
      const messages = querySnapshot.docs.map((doc) => doc.data());
      return { messages, limitReached: false };
    }

    // 以下は talk room の場合のみ（制限対象）
    const chatHistoryLimit = state.subscription.accountLimits.chatHistoryLimit;
    const limitValue = chatHistoryLimit?.limit ?? -1;
    const used = chatHistoryLimit?.used ?? 0;
    const unlimited = chatHistoryLimit?.unlimited ?? false;

    // 制限チェック（-1 は無制限、unlimited も無制限）
    if (!unlimited && limitValue !== -1 && used >= limitValue) {
      return { messages: [], limitReached: true };
    }

    const queryMessage = query(
      collection(db, "rooms", roomId, "messages"),
      where("created_at", "<", lastMessageSyncAt),
      orderBy("created_at", "desc"),
      limit(messageCount),
    );
    const querySnapshot = await getDocs(queryMessage);
    const messages = querySnapshot.docs.map((doc) => doc.data());

    return { messages, limitReached: false };
  },
);

export const markParentChatRoomMessage = createAsyncThunk<any, void>(
  "message/markParentChatRoomMessage",
  async (_, thunkAPI) => {
    const token = await fetchAuthToken();
    const { message } = thunkAPI.getState() as RootState;
    const chatRoomsReadAt = message.chatRoomMessageSync.ids
      .map((id) => message.chatRoomMessageSync.entities[id])
      .filter(
        (sync) =>
          sync.lastReadAt !== sync.latestReadAt ||
          (sync.lastReadAt === null && sync.latestReadAt !== null),
      )
      .map((sync) => ({
        chat_room_id: sync.id,
        uid: sync.uid,
        last_read_at: sync.latestReadAt,
      }));
    const response = await client({ token: token }).put(`/v1/chat/room/marked`, {
      chatRoomsReadAt,
    });
    return response.data.data;
  },
);

/**
 * チャットルームにメッセージを送信
 */
export const sendChatRoomMessage = async ({
  roomId,
  message,
}: {
  roomId: string;
  message: {};
}) => {
  const token = await fetchAuthToken();
  return client({ token: token }).post(
    `/v1/chat/room/message/${roomId}`,
    message,
  );
};
